import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { Input } from '@heroui/input';
import { Checkbox } from '@heroui/checkbox';
import { addToast } from '@heroui/toast';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '@/contexts/auth-context';
import { addOrUpdateCartItemsBulk, addOrUpdateGuestCartItemsBulk, createOrder, createGuestOrder, validatePromoCode, checkMemberPricing } from '@/apiEndpoints';
import getStripe from '@/get-stripejs';
import Close from '@/icons/Close';
import { IconMinus as Minus, IconPlus as Plus } from '@tabler/icons-react';
import { useTheme } from '@/contexts/theme-context';
import { calculateFeePerUnit, stripeFeeForNet } from '@/utils/fees';
import { currencySymbol } from '@/utils/currency';
import { btnPrimary, cardTitle } from '@/components/ui/design';

const fieldClassNames = {
    label: 'text-grey40 dark:text-nightMuted',
    input: 'text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
    inputWrapper: 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 group-data-[invalid=true]:ring-2 group-data-[invalid=true]:ring-red-400 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.06] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
};
const closeBtn = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-grey30 ring-1 ring-black/[0.06] transition-colors hover:bg-grey99 dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/[0.06]';
const sectionLabel = 'text-[11px] font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted';
const seatChip = 'inline-flex items-center h-7 px-3 rounded-full text-xs font-semibold bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent';
const stepBtn = 'flex h-9 w-9 items-center justify-center rounded-full bg-grey10 text-white transition-colors hover:bg-black disabled:opacity-30 disabled:cursor-not-allowed dark:bg-white dark:text-nightBg dark:hover:bg-grey90';

interface GetTicketsModalProps {
    isOpen: boolean;
    onClose: () => void;
    event: any;
    tickets: any[];
    selectedTime: string | null;
    selectedDateForApi: string;
    initialTicketCounts?: { [ticketId: string]: number };
    initialPromoCode?: string;
    selectedSeats?: SelectedSeat[];
}

export type SelectedSeat = { seatUuid: string; ticketId: string; label: string; price?: number };

type AttendeeInfo = { name: string; last: string; email: string; phone: string };
type Details = { name: string; last: string; email: string; phone: string };
interface ValidationErrors { name?: string; last?: string; email?: string; phone?: string }

const ATTENDEE_ORDINALS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

function getEventDateTimeLabel(event: any, selectedTime: string | null) {
    if (!selectedTime) return '';
    const start = new Date(selectedTime);
    const matchedRange = (event?.times || []).find((range: any) => {
        const rStart = new Date(range.startDate);
        const rEnd = new Date(range.endDate);
        const rStartDay = new Date(rStart.getFullYear(), rStart.getMonth(), rStart.getDate());
        const rEndDay = new Date(rEnd.getFullYear(), rEnd.getMonth(), rEnd.getDate());
        const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        return startDay >= rStartDay && startDay <= rEndDay;
    });
    const dateLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const startTimeLabel = moment(start).format('h:mm A');
    if (matchedRange) {
        const endTimeSrc = new Date(matchedRange.endDate);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), endTimeSrc.getHours(), endTimeSrc.getMinutes());
        return `${dateLabel} - ${startTimeLabel} to ${moment(end).format('h:mm A')}`;
    }
    return `${dateLabel} - ${startTimeLabel}`;
}

// Embedded Stripe payment form, rendered inside <Elements>. Mirrors the
// court-reservation-web PaymentForm pattern: PaymentElement + confirmPayment
// with redirect: 'if_required' so card payments stay inline.
function CheckoutPaymentForm({ amount, currency, orderId, onSuccess, onError }: { amount: number; currency: string; orderId: string; onSuccess: () => void; onError: (message: string) => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isElementReady, setIsElementReady] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements || !isElementReady) return;

        setIsProcessing(true);
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/order?id=${orderId}&from=payment`,
            },
            redirect: 'if_required',
        });

        if (error) {
            onError(error.message || 'Payment failed. Please check your card details and try again.');
            setIsProcessing(false);
        } else if (paymentIntent?.status === 'succeeded') {
            onSuccess();
        } else {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-4">
                <PaymentElement
                    options={{ layout: 'tabs', wallets: { applePay: 'auto', googlePay: 'auto' } }}
                    onReady={() => setIsElementReady(true)}
                />
            </div>
            <button
                type="submit"
                disabled={!stripe || !isElementReady || isProcessing}
                className={`${btnPrimary} w-full`}
            >
                {!isElementReady ? 'Loading payment form...' : isProcessing ? 'Processing...' : `Pay ${currency}${amount.toFixed(2)}`}
            </button>
        </form>
    );
}

export default function GetTicketsModal({ isOpen, onClose, event, tickets, selectedTime, selectedDateForApi, initialTicketCounts, initialPromoCode, selectedSeats = [] }: GetTicketsModalProps) {
    const { theme } = useTheme();
    const cur = currencySymbol(event?.currency);
    const { user, isAuthenticated, guestCartId } = useAuth();
    const isGuest = !isAuthenticated;

    const [ticketCounts, setTicketCounts] = useState<{ [ticketId: string]: number }>(initialTicketCounts || {});
    const [attendeeInfos, setAttendeeInfos] = useState<{ [key: string]: AttendeeInfo }>({});
    const [usePerTicketInfo, setUsePerTicketInfo] = useState(false);
    const [details, setDetails] = useState<Details>({ name: '', last: '', email: '', phone: '' });
    const [useCustomInfo, setUseCustomInfo] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [showAttendeeValidation, setShowAttendeeValidation] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [appliedPromoCode, setAppliedPromoCode] = useState<any>(null);
    const [promoCodeError, setPromoCodeError] = useState('');
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);
    const [pendingPromoApply, setPendingPromoApply] = useState(false);
    const [memberPricingResult, setMemberPricingResult] = useState<any>(null);
    const [waiverAccepted, setWaiverAccepted] = useState(false);
    const [showWaiverModal, setShowWaiverModal] = useState(false);

    // Pre-fill details for authenticated users, mirroring Cart.tsx
    useEffect(() => {
        if (isOpen) {
            setTicketCounts(initialTicketCounts || {});
            setAttendeeInfos({});
            setShowAttendeeValidation(false);
            if (initialPromoCode) {
                setPromoCode(initialPromoCode);
                setPendingPromoApply(true);
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (pendingPromoApply && promoCode) {
            setPendingPromoApply(false);
            validatePromo();
        }
    }, [pendingPromoApply, promoCode]);

    useEffect(() => {
        if (user && !isGuest) {
            setDetails({ name: user.firstName || '', last: '', email: user.email || '', phone: '' });
        }
    }, [user, isGuest]);

    const eventId = event?._id || event?.id || '';
    const waiverRequired = Boolean(event?.liabilityWaiverEnabled);
    const requiredTicketInfoMode = Number(event?.ticketInfoMode || 0);
    const perTicketInfoEnabled = requiredTicketInfoMode === 2 ? true : usePerTicketInfo;

    const seatMode = selectedSeats.length > 0;
    const counts: { [ticketId: string]: number } = seatMode
        ? selectedSeats.reduce((acc, seat) => ({ ...acc, [seat.ticketId]: (acc[seat.ticketId] || 0) + 1 }), {} as { [ticketId: string]: number })
        : ticketCounts;

    const selectedTickets = (tickets || []).filter((t: any) => (counts[t._id] || 0) > 0);
    const unitPricesOf = (ticket: any): number[] => {
        const ticketPrice = Number(ticket.price || 0);
        return seatMode
            ? selectedSeats.filter(seat => seat.ticketId === ticket._id).map(seat => (typeof seat.price === 'number' ? seat.price : ticketPrice))
            : Array.from({ length: counts[ticket._id] || 0 }, () => ticketPrice);
    };
    const lineTotalOf = (ticket: any) => unitPricesOf(ticket).reduce((sum, price) => sum + price, 0);
    const formatAmount = (amount: number) => `${cur}${amount % 1 ? amount.toFixed(2) : amount.toFixed(0)}`;
    const ticketPriceLabel = (ticket: any) => {
        const prices = seatMode ? unitPricesOf(ticket) : [Number(ticket.price || 0)];
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        return min === max ? formatAmount(min) : `${formatAmount(min)} - ${formatAmount(max)}`;
    };
    const subtotal = selectedTickets.reduce((sum: number, t: any) => sum + lineTotalOf(t), 0);

    const buildCartItems = () => seatMode
        ? selectedSeats.map(seat => ({
            eventId,
            ticketId: seat.ticketId,
            quantity: 1,
            selectedDate: selectedDateForApi,
            seatUuid: seat.seatUuid,
        }))
        : selectedTickets.map((ticket: any) => ({
            eventId,
            ticketId: ticket._id,
            quantity: ticketCounts[ticket._id],
            selectedDate: selectedDateForApi,
        }));
    const hasSelection = selectedTickets.length > 0;

    const getTicketAttendeeSlots = (ticket: any) => {
        const ticketSeatList = seatMode ? selectedSeats.filter(seat => seat.ticketId === ticket._id) : [];
        const count = counts[ticket._id] || 0;
        return Array.from({ length: count }, (_, index) => {
            const seatUuid = ticketSeatList[index]?.seatUuid;
            return { key: `${ticket._id}:${seatUuid || index}`, ticketId: ticket._id as string, index, seatUuid };
        });
    };
    const attendeeSlots = selectedTickets.flatMap(getTicketAttendeeSlots);
    const isAttendeeComplete = (info?: AttendeeInfo) =>
        Boolean(info?.name?.trim() && info?.last?.trim() && info?.email?.trim() && info?.phone?.trim());

    // Member pricing is matched automatically off the buyer's email, mirroring Cart.tsx
    const buyerEmail = (isGuest || useCustomInfo) ? details.email.trim() : (user?.email || '');
    const selectionKey = selectedTickets.map((t: any) => `${t._id}:${counts[t._id]}`).join(',');
    useEffect(() => {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail);
        if (!isValidEmail || !eventId || selectedTickets.length === 0) {
            setMemberPricingResult(null);
            return;
        }
        const timeoutId = setTimeout(async () => {
            try {
                const items = selectedTickets.map((t: any) => ({ eventId, ticketId: t._id, quantity: counts[t._id] || 0 }));
                setMemberPricingResult(await checkMemberPricing({ email: buyerEmail, items }));
            } catch {
                setMemberPricingResult(null);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerEmail, eventId, selectionKey]);

    // Per-unit discount breakdown for a ticket, replicating the backend's combination rule (orders.service.ts):
    // member pricing wins by default; if the ticket allows it, the promo code stacks on the member-discounted remainder.
    const getItemDiscountPerUnit = (ticket: any, unitPrice: number) => {
        const memberMatch = memberPricingResult?.tickets?.find((t: any) => t.ticketId === ticket._id && t.isMatch) || null;
        const memberPerUnit = memberMatch?.discountAmountPerUnit || 0;

        const ticketPromo = appliedPromoCode?.ticketDiscounts?.find((d: any) => d.ticketId === ticket._id);
        const promoDiscountType = appliedPromoCode?.discountType;
        const promoDiscountValue = appliedPromoCode?.discountValue;
        const promoPerUnitOnFull = (ticketPromo && promoDiscountType && promoDiscountValue)
            ? (promoDiscountType === 'fixed' ? Math.min(promoDiscountValue, unitPrice) : (unitPrice * promoDiscountValue) / 100)
            : 0;

        let memberContribution = 0;
        let promoContribution = 0;
        if (memberPerUnit > 0 && promoPerUnitOnFull > 0 && memberMatch.allowPromoCodeWithMemberPricing) {
            const remaining = Math.max(0, unitPrice - memberPerUnit);
            promoContribution = promoDiscountType === 'fixed'
                ? Math.min(promoDiscountValue, remaining)
                : (remaining * promoDiscountValue) / 100;
            memberContribution = memberPerUnit;
        } else if (memberPerUnit > 0) {
            memberContribution = memberPerUnit;
        } else {
            promoContribution = promoPerUnitOnFull;
        }
        return { memberContribution, promoContribution };
    };

    const pricing = selectedTickets.reduce((acc: { promo: number; member: number }, t: any) => {
        unitPricesOf(t).forEach(unitPrice => {
            const { memberContribution, promoContribution } = getItemDiscountPerUnit(t, unitPrice);
            acc.promo += promoContribution;
            acc.member += memberContribution;
        });
        return acc;
    }, { promo: 0, member: 0 });
    const discount = pricing.promo;
    const memberDiscount = pricing.member;
    // Card processing fees and 'included' service fees are part of the ticket price; 'added' service fees are charged on top
    const discountedTotal = Math.max(0, subtotal - discount - memberDiscount);
    const taxInfo = event?.taxInfo;
    const resolvedFee = event?.resolvedFee || taxInfo?.resolvedFee;
    // One buyer-facing fee: the organizer's added service fee plus Stripe's processing cost, grossed up so the
    // tickets and service fee remain whole after Stripe's cut. Tax is not shown.
    const organizerFee = taxInfo?.feeMode === 'added'
        ? selectedTickets.reduce((sum: number, t: any) => sum + unitPricesOf(t).reduce((ticketSum, unitPrice) => {
            const { memberContribution, promoContribution } = getItemDiscountPerUnit(t, unitPrice);
            return ticketSum + calculateFeePerUnit(resolvedFee, Math.max(0, unitPrice - memberContribution - promoContribution));
        }, 0), 0)
        : 0;
    const addedFee = taxInfo?.feeMode === 'added'
        ? Math.round((organizerFee + stripeFeeForNet(discountedTotal + organizerFee, taxInfo)) * 100) / 100
        : 0;
    const total = discountedTotal + addedFee;

    const updateCount = (ticketId: string, delta: number, max: number) => {
        setTicketCounts(prev => {
            const next = Math.max(0, Math.min(max, (prev[ticketId] || 0) + delta));
            return { ...prev, [ticketId]: next };
        });
    };

    const updateAttendee = (key: string, patch: Partial<AttendeeInfo>) => {
        setAttendeeInfos(prev => {
            const existing = prev[key] || { name: '', last: '', email: '', phone: '' };
            return { ...prev, [key]: { ...existing, ...patch } };
        });
    };

    const updateDetail = (field: keyof Details, value: string) => {
        setDetails(d => ({ ...d, [field]: value }));
        if (showValidationErrors && validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validatePromo = async () => {
        if (!promoCode.trim()) {
            setPromoCodeError('');
            setAppliedPromoCode(null);
            return;
        }
        if (!hasSelection) {
            setPromoCodeError('Please select at least one ticket first.');
            return;
        }
        setIsValidatingPromo(true);
        setPromoCodeError('');
        try {
            // The backend checks promo codes against the persisted cart, but tickets picked in
            // this modal only live in local state until Checkout. Sync them into the cart first
            // (same call Checkout itself makes) so the validation has something to match against.
            const items = buildCartItems();

            if (isAuthenticated) {
                await addOrUpdateCartItemsBulk({ items });
            } else if (guestCartId) {
                await addOrUpdateGuestCartItemsBulk({ guestCartId, items });
            }

            const result = await validatePromoCode({
                code: promoCode.trim(),
                userId: user?.userId,
                guestCartId,
                orderAmount: subtotal,
                email: buyerEmail || undefined,
            });
            if (result.isValid) {
                setAppliedPromoCode(result);
                setPromoCodeError('');
            } else {
                setAppliedPromoCode(null);
                setPromoCodeError(result.error || 'Invalid promo code');
            }
        } catch {
            setAppliedPromoCode(null);
            setPromoCodeError('Error validating promo code');
        } finally {
            setIsValidatingPromo(false);
        }
    };

    // Mirrors Cart.tsx's validateForm
    const validateForm = (): boolean => {
        const errors: ValidationErrors = {};

        if (isGuest || useCustomInfo) {
            if (!details.name.trim()) errors.name = 'Name is required';
            if (!details.last.trim()) errors.last = 'Last name is required';
            if (!details.email.trim()) {
                errors.email = 'Email is required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) {
                errors.email = 'Please enter a valid email address';
            }
            if (!details.phone.trim()) {
                errors.phone = 'Phone number is required';
            } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(details.phone.replace(/\s/g, ''))) {
                errors.phone = 'Please enter a valid phone number';
            }
        }

        setValidationErrors(errors);
        setShowValidationErrors(true);

        if (perTicketInfoEnabled) {
            setShowAttendeeValidation(true);
            const missing = attendeeSlots.some(slot => !isAttendeeComplete(attendeeInfos[slot.key]));
            if (missing) {
                addToast({ title: 'Missing attendee info', description: 'Please fill in all attendee fields for each ticket.', color: 'danger' });
                return false;
            }
        }

        return Object.keys(errors).length === 0;
    };

    const [pendingOrderRes, setPendingOrderRes] = useState<any>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Builds the order (and, for paid orders, a Stripe PaymentIntent) from the
    // current selection. Runs when "Check Out" is clicked.
    const createOrderMutation = useMutation({
        mutationFn: async () => {
            if (!hasSelection) {
                throw new Error('Please select at least one ticket.');
            }

            if (!validateForm()) {
                throw new Error('VALIDATION_FAILED');
            }

            if (waiverRequired && !waiverAccepted) {
                throw new Error('WAIVER_REQUIRED');
            }

            const items = buildCartItems();

            if (isAuthenticated) {
                await addOrUpdateCartItemsBulk({ items });
            } else if (guestCartId) {
                await addOrUpdateGuestCartItemsBulk({ guestCartId, items });
            } else {
                throw new Error('Unable to start checkout. Please refresh the page and try again.');
            }

            const promoCodeValue = appliedPromoCode?.promoCode?.code || null;
            const attendees = perTicketInfoEnabled
                ? attendeeSlots.map(slot => {
                    const info = attendeeInfos[slot.key] || { name: '', last: '', email: '', phone: '' };
                    return {
                        eventId,
                        ticketId: slot.ticketId,
                        selectedDate: selectedDateForApi,
                        index: slot.index,
                        ...(slot.seatUuid ? { seatUuid: slot.seatUuid } : {}),
                        name: `${info.name} ${info.last}`.trim(),
                        email: info.email.trim(),
                        phone: info.phone.trim(),
                    };
                })
                : undefined;

            const waiverFields = waiverRequired
                ? { acceptedLiabilityWaiver: waiverAccepted, acceptedLiabilityWaiverAt: new Date().toISOString() }
                : {};

            const billingName = isGuest || useCustomInfo ? `${details.name} ${details.last}`.trim() : (user?.firstName || '');
            const billingEmail = isGuest || useCustomInfo ? details.email : (user?.email || '');
            const billingPhone = isGuest || useCustomInfo ? details.phone : '';
            const expectedTicketAmount = Math.round(subtotal * 100) / 100;

            return isGuest
                ? await createGuestOrder({
                    guestCartId: guestCartId as string,
                    guestName: billingName,
                    guestEmail: billingEmail,
                    guestPhone: billingPhone,
                    promoCode: promoCodeValue,
                    attendees,
                    paymentType: 'payment_intent',
                    expectedTicketAmount,
                    ...waiverFields,
                })
                : await createOrder({
                    customerEmail: billingEmail,
                    customerPhone: billingPhone,
                    customerName: billingName,
                    promoCode: promoCodeValue,
                    attendees,
                    paymentType: 'payment_intent',
                    expectedTicketAmount,
                    ...waiverFields,
                });
        },
        onSuccess: async (res: any) => {
            const clientSecret = res?.data?.stripe?.clientSecret;
            const sessionId = res?.data?.stripe?.sessionId;
            if (clientSecret) {
                setPendingOrderRes(res);
                setShowPaymentModal(true);
            } else if (total === 0 && res?.data?.order?._id) {
                window.location.href = `/order?id=${res.data.order._id}&from=payment`;
            } else if (sessionId) {
                // Backend responded with a Checkout Session instead of a PaymentIntent
                // (i.e. it doesn't support paymentType: 'payment_intent' yet) — fall
                // back to Stripe's hosted Checkout so the order still gets paid.
                const stripe = await getStripe(res?.data?.stripe?.publishableKey);
                await stripe?.redirectToCheckout({ sessionId });
            } else {
                addToast({ title: 'Checkout Error', description: 'Unable to start payment. Please try again.', color: 'danger' });
            }
        },
        onError: (err: any) => {
            if (err.message === 'VALIDATION_FAILED') {
                // Field-level errors are already shown inline
            } else if (err.message === 'WAIVER_REQUIRED') {
                addToast({ title: 'Liability waiver required', description: "Please agree to the event's liability waiver to continue.", color: 'danger' });
            } else if (err.message === 'Please select at least one ticket.') {
                addToast({ title: 'No tickets selected', description: err.message, color: 'danger' });
            } else {
                const msg = err?.response?.data?.message || err?.message || 'Failed to start checkout. Please try again.';
                addToast({ title: 'Checkout Error', description: msg, color: 'danger' });
            }
        },
    });

    const handlePaymentSuccess = () => {
        const orderId = pendingOrderRes?.data?.order?._id;
        if (orderId) {
            window.location.href = `/order?id=${orderId}&from=payment`;
        }
    };

    const handlePaymentError = (message: string) => {
        addToast({ title: 'Payment Error', description: message, color: 'danger' });
    };

    if (!isOpen) return null;

    const waiverAndTerms = (
        <>
            {waiverRequired && (
                <div className="flex items-start gap-2">
                    <Checkbox isSelected={waiverAccepted} onValueChange={setWaiverAccepted} size="sm" />
                    <span className="text-xs text-grey30 dark:text-nightMuted">
                        I agree to the{' '}
                        <button
                            type="button"
                            onClick={() => setShowWaiverModal(true)}
                            className="text-primary dark:text-nightAccent font-semibold underline underline-offset-2"
                        >
                            Event's Liability Waiver.
                        </button>
                    </span>
                </div>
            )}

            <p className="text-[11px] leading-relaxed text-grey40 dark:text-nightMuted">
                By purchasing you agree to Eventup general <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-primary40 dark:text-nightAccent underline font-semibold">Terms of Use</a>, <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary40 dark:text-nightAccent underline font-semibold">Privacy Policy</a> and the Ticket Purchase Terms.
            </p>
        </>
    );

    const checkoutButton = (
        <button
            className={`${btnPrimary} w-full text-[15px]`}
            onClick={() => createOrderMutation.mutate()}
            disabled={createOrderMutation.isPending}
        >
            {createOrderMutation.isPending ? 'Processing...' : 'Check Out'}
        </button>
    );

    return (
        <div className="fixed inset-x-0 top-0 h-[100dvh] md:h-auto md:inset-0 bg-black/50 flex items-stretch md:items-center justify-center z-[10000] p-0 md:p-4">
            <div className="bg-white dark:bg-nightBg rounded-none md:rounded-[32px] w-full h-full md:h-auto max-h-none md:max-h-[80vh] max-w-none md:max-w-4xl shadow-2xl md:ring-1 md:ring-black/[0.06] dark:md:ring-white/10 flex flex-col md:flex-row overflow-hidden transition-colors">
                {/* Mobile-only header: stays pinned while the body below scrolls */}
                <div className="md:hidden flex items-center justify-between px-5 py-4 border-b border-black/[0.06] dark:border-white/10 shrink-0">
                    <h2 className="text-[26px] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white">Get Tickets</h2>
                    <button onClick={onClose} className={closeBtn} disabled={createOrderMutation.isPending}>
                        <Close width={20} height={20} />
                    </button>
                </div>

                {/* Body: single scroll region on mobile (both columns together); two independently-laid-out columns on desktop */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain md:overflow-hidden flex flex-col md:flex-row">
                    {/* Left: tickets + attendee info + your information */}
                    <div className="flex-1 min-w-0 bg-white dark:bg-nightBg flex flex-col md:min-h-0">
                        <h2 className="hidden md:block text-[36px] font-bold leading-none tracking-[-0.045em] text-grey10 dark:text-white px-8 pt-8">Get Tickets</h2>
                        <div className="p-5 md:px-8 md:pb-8 md:pt-6 flex flex-col gap-4 md:flex-1 md:overflow-y-auto">
                            <div className="flex items-center gap-4 rounded-[24px] bg-grey99 p-3 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                {event?.attachments?.banner?.link && (
                                    <img src={event.attachments.banner.link} alt={event?.title} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <div className="font-bold text-[15px] tracking-[-0.01em] text-grey10 dark:text-white truncate">{event?.title}</div>
                                    <div className="mt-0.5 text-[13px] font-medium text-grey40 dark:text-nightMuted">{getEventDateTimeLabel(event, selectedTime)}</div>
                                </div>
                            </div>

                            {requiredTicketInfoMode > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer rounded-2xl px-1">
                                    <Checkbox
                                        isSelected={perTicketInfoEnabled}
                                        isDisabled={requiredTicketInfoMode === 2}
                                        onValueChange={checked => setUsePerTicketInfo(!!checked)}
                                        size="sm"
                                    />
                                    <span className="text-sm text-grey30 dark:text-nightMuted">
                                        {requiredTicketInfoMode === 2 ? 'Attendee info per ticket is required for this event' : 'Enter attendee info per ticket'}
                                    </span>
                                </label>
                            )}

                            {(seatMode ? selectedTickets : tickets || []).map((ticket: any) => {
                                const count = counts[ticket._id] || 0;
                                const ticketSeats = seatMode ? selectedSeats.filter(seat => seat.ticketId === ticket._id) : [];
                                const soldOut = ticket.remained === 0;
                                const now = new Date();
                                const saleStartDate = ticket.saleStart ? new Date(ticket.saleStart) : null;
                                const saleEndDate = ticket.saleEnd ? new Date(ticket.saleEnd) : null;
                                const isSaleNotStarted = Boolean(saleStartDate && now < saleStartDate);
                                const isSaleEnded = Boolean(saleEndDate && now > saleEndDate);
                                const isTicketInactive = ticket.status === false;
                                const isDisabled = soldOut || isSaleNotStarted || isSaleEnded || isTicketInactive;

                                return (
                                    <div key={ticket._id} className={`rounded-[24px] bg-white p-4 ring-1 transition-shadow dark:bg-white/[0.04] ${count > 0 ? 'ring-2 ring-grey10 dark:ring-nightAccent' : 'ring-black/[0.06] dark:ring-white/10'}`}>
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="font-bold tracking-[-0.02em] text-grey10 dark:text-white text-[16px]">{ticket.name}</div>
                                                <div className="mt-0.5 text-primary dark:text-nightAccent font-bold tracking-[-0.02em] text-[18px]">{ticketPriceLabel(ticket)}</div>
                                                {ticket.refundPolicy && (
                                                    <div className="mt-1 text-xs text-grey40 dark:text-nightMuted">{ticket.refundPolicy}</div>
                                                )}
                                                {/* {(ticket.saleStart || ticket.saleEnd) && (
                                                <div className="text-xs text-grey40 mt-1">
                                                    {ticket.saleStart && <div>Sale starts: {new Date(ticket.saleStart).toLocaleDateString()} {new Date(ticket.saleStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>}
                                                    {ticket.saleEnd && <div>Sale ends: {new Date(ticket.saleEnd).toLocaleDateString()} {new Date(ticket.saleEnd).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>}
                                                </div>
                                            )} */}
                                                {seatMode && !perTicketInfoEnabled && (
                                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                                        {ticketSeats.map(seat => (
                                                            <span key={seat.seatUuid} className={seatChip}>
                                                                {seat.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {seatMode ? (
                                                <span className="flex h-9 min-w-[44px] items-center justify-center rounded-full bg-grey99 px-3 font-bold text-sm text-grey10 dark:bg-white/[0.06] dark:text-white shrink-0">x{count}</span>
                                            ) : isDisabled ? (
                                                <span className="flex h-9 shrink-0 items-center rounded-full bg-red-50 px-3 text-[11px] font-bold uppercase tracking-[0.1em] text-[#CB0000] whitespace-nowrap dark:bg-red-950/30 dark:text-red-400">
                                                    {soldOut ? 'Sold Out' : isSaleNotStarted ? 'Sale Not Started' : isSaleEnded ? 'Sale Ended' : isTicketInactive ? 'Ticket Inactive' : 'Unavailable'}
                                                </span>
                                            ) : (
                                                <div className="flex h-11 shrink-0 items-center gap-1 rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
                                                    <button
                                                        className={stepBtn}
                                                        onClick={() => updateCount(ticket._id, -1, ticket.remained)}
                                                        disabled={count <= 0 || showPaymentModal}
                                                    >
                                                        <Minus className="h-4 w-4" strokeWidth={2.5} />
                                                    </button>
                                                    <span className="font-bold text-[15px] min-w-[28px] text-center text-grey10 dark:text-white">{count}</span>
                                                    <button
                                                        className={stepBtn}
                                                        onClick={() => updateCount(ticket._id, 1, ticket.remained)}
                                                        disabled={count >= ticket.remained || showPaymentModal}
                                                    >
                                                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {count > 0 && perTicketInfoEnabled && (
                                            <div className="mt-4 flex flex-col gap-5 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                                {getTicketAttendeeSlots(ticket).map(({ key, index: idx }) => {
                                                    const val = attendeeInfos[key] || { name: '', last: '', email: '', phone: '' };
                                                    const showInvalid = (field: keyof AttendeeInfo) => showAttendeeValidation && !val[field].trim();
                                                    return (
                                                        <div key={key}>
                                                            {seatMode && ticketSeats[idx] ? (
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className={seatChip}>
                                                                        {ticketSeats[idx].label}
                                                                    </span>
                                                                    <span className={sectionLabel}>Attendee Information</span>
                                                                </div>
                                                            ) : (
                                                                <div className={`${sectionLabel} mb-2`}>
                                                                    {count === 1 ? 'Attendee Information' : `Attendee ${ATTENDEE_ORDINALS[idx] || idx + 1}`}
                                                                </div>
                                                            )}
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                <Input
                                                                    size="sm"
                                                                    isRequired
                                                                    label="Name"
                                                                    placeholder="Name"
                                                                    value={val.name}
                                                                    isInvalid={showInvalid('name')}
                                                                    onChange={(e) => updateAttendee(key, { name: e.target.value })}
                                                                    classNames={fieldClassNames}
                                                                />
                                                                <Input
                                                                    size="sm"
                                                                    isRequired
                                                                    label="Last name"
                                                                    placeholder="Last name"
                                                                    value={val.last}
                                                                    isInvalid={showInvalid('last')}
                                                                    onChange={(e) => updateAttendee(key, { last: e.target.value })}
                                                                    classNames={fieldClassNames}
                                                                />
                                                                <Input
                                                                    size="sm"
                                                                    isRequired
                                                                    type="email"
                                                                    label="Email"
                                                                    placeholder="e.g. name@gmail.com"
                                                                    value={val.email}
                                                                    isInvalid={showInvalid('email')}
                                                                    onChange={(e) => updateAttendee(key, { email: e.target.value })}
                                                                    classNames={fieldClassNames}
                                                                />
                                                                <Input
                                                                    size="sm"
                                                                    isRequired
                                                                    type="tel"
                                                                    label="Phone"
                                                                    placeholder="e.g. +31 0123 456 78"
                                                                    value={val.phone}
                                                                    isInvalid={showInvalid('phone')}
                                                                    onChange={(e) => updateAttendee(key, { phone: e.target.value })}
                                                                    classNames={fieldClassNames}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Your Information: guest contact form, or account info with custom-info toggle */}
                            {isGuest ? (
                                <div className="border-t border-black/[0.06] dark:border-white/10 pt-5 mt-1">
                                    <div className={`${sectionLabel} mb-3`}>Your Information</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Input
                                            size="sm"
                                            label="Name"
                                            placeholder="Name"
                                            value={details.name}
                                            isInvalid={showValidationErrors && !!validationErrors.name}
                                            errorMessage={showValidationErrors && validationErrors.name}
                                            onChange={(e) => updateDetail('name', e.target.value)}
                                            classNames={fieldClassNames}
                                        />
                                        <Input
                                            size="sm"
                                            label="Last name"
                                            placeholder="Last name"
                                            value={details.last}
                                            isInvalid={showValidationErrors && !!validationErrors.last}
                                            errorMessage={showValidationErrors && validationErrors.last}
                                            onChange={(e) => updateDetail('last', e.target.value)}
                                            classNames={fieldClassNames}
                                        />
                                        <Input
                                            size="sm"
                                            type="email"
                                            label="Email"
                                            placeholder="e.g. name@gmail.com"
                                            value={details.email}
                                            isInvalid={showValidationErrors && !!validationErrors.email}
                                            errorMessage={showValidationErrors && validationErrors.email}
                                            onChange={(e) => updateDetail('email', e.target.value)}
                                            classNames={fieldClassNames}
                                        />
                                        <Input
                                            size="sm"
                                            type="tel"
                                            label="Phone"
                                            placeholder="e.g. +31 0123 456 78"
                                            value={details.phone}
                                            isInvalid={showValidationErrors && !!validationErrors.phone}
                                            errorMessage={showValidationErrors && validationErrors.phone}
                                            onChange={(e) => updateDetail('phone', e.target.value)}
                                            classNames={fieldClassNames}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="border-t border-black/[0.06] dark:border-white/10 pt-5 mt-1">
                                    <div className={`${sectionLabel} mb-3`}>Your Information</div>
                                    <label className="flex items-center gap-2 cursor-pointer mb-4">
                                        <Checkbox isSelected={useCustomInfo} onValueChange={checked => setUseCustomInfo(!!checked)} size="sm" />
                                        <span className="text-sm text-grey30 dark:text-nightMuted">Use custom order information</span>
                                    </label>

                                    {!useCustomInfo ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-grey40 dark:text-nightMuted mb-1.5">Name</label>
                                                <div className="w-full truncate rounded-2xl bg-grey99 px-4 py-3 text-sm text-grey10 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-white dark:ring-white/10">{user?.firstName || 'Not provided'}</div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-grey40 dark:text-nightMuted mb-1.5">Email</label>
                                                <div className="w-full truncate rounded-2xl bg-grey99 px-4 py-3 text-sm text-grey10 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-white dark:ring-white/10">{user?.email || 'Not provided'}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <Input
                                                size="sm"
                                                label="Name"
                                                placeholder="Name"
                                                value={details.name}
                                                isInvalid={showValidationErrors && !!validationErrors.name}
                                                errorMessage={showValidationErrors && validationErrors.name}
                                                onChange={(e) => updateDetail('name', e.target.value)}
                                                classNames={fieldClassNames}
                                            />
                                            <Input
                                                size="sm"
                                                label="Last name"
                                                placeholder="Last name"
                                                value={details.last}
                                                isInvalid={showValidationErrors && !!validationErrors.last}
                                                errorMessage={showValidationErrors && validationErrors.last}
                                                onChange={(e) => updateDetail('last', e.target.value)}
                                                classNames={fieldClassNames}
                                            />
                                            <Input
                                                size="sm"
                                                type="email"
                                                label="Email"
                                                placeholder="e.g. name@gmail.com"
                                                value={details.email}
                                                isInvalid={showValidationErrors && !!validationErrors.email}
                                                errorMessage={showValidationErrors && validationErrors.email}
                                                onChange={(e) => updateDetail('email', e.target.value)}
                                                classNames={fieldClassNames}
                                            />
                                            <Input
                                                size="sm"
                                                type="tel"
                                                label="Phone"
                                                placeholder="e.g. +31 0123 456 78"
                                                value={details.phone}
                                                isInvalid={showValidationErrors && !!validationErrors.phone}
                                                errorMessage={showValidationErrors && validationErrors.phone}
                                                onChange={(e) => updateDetail('phone', e.target.value)}
                                                classNames={fieldClassNames}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: order summary */}
                    <div className="w-full md:w-[340px] shrink-0 bg-grey99 dark:bg-nightSurface p-5 md:p-6 flex flex-col gap-5">
                        <div className="flex items-center justify-between">
                            <div className={cardTitle}>Your Order</div>
                            <button onClick={onClose} className={`${closeBtn} hidden md:flex`} disabled={createOrderMutation.isPending}>
                                <Close width={20} height={20} />
                            </button>
                        </div>

                        <div className="flex flex-col gap-2.5 text-sm">
                            {selectedTickets.length === 0 ? (
                                <div className="text-grey40 dark:text-nightMuted">No tickets selected yet.</div>
                            ) : selectedTickets.map((ticket: any) => (
                                <div key={ticket._id} className="flex items-center justify-between gap-3">
                                    <span className="min-w-0 text-grey30 dark:text-nightTextSecondary">{counts[ticket._id]} x {ticket.name}</span>
                                    <span className="shrink-0 font-bold text-grey10 dark:text-white">{cur}{lineTotalOf(ticket).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div>
                            <div className={`${sectionLabel} mb-2`}>Promo code</div>
                            <div className="flex items-center gap-2 pl-2 pr-1.5 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 h-12">
                                <Input
                                    className="no-global-input"
                                    size="sm"
                                    variant='bordered'
                                    placeholder="Enter promo code"
                                    classNames={{
                                        inputWrapper: 'bg-transparent rounded-full border-none shadow-none h-12 hover:bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent',
                                        input: 'text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted'
                                    }}
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value)}
                                />
                                <button
                                    onClick={validatePromo}
                                    disabled={isValidatingPromo || showPaymentModal}
                                    className="h-9 rounded-full bg-grey10 px-4 text-white font-semibold text-sm shrink-0 transition-colors hover:bg-black disabled:opacity-50 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                                >
                                    {isValidatingPromo ? '...' : 'Apply'}
                                </button>
                            </div>
                            {promoCodeError && <div className="text-red-500 dark:text-red-400 text-xs mt-2 px-2">{promoCodeError}</div>}
                            {appliedPromoCode && <div className="text-green-600 dark:text-green-400 text-xs font-semibold mt-2 px-2">Promo code applied</div>}
                        </div>

                        <div className="border-t border-black/[0.06] dark:border-white/10 pt-4 flex flex-col gap-1.5 text-[14px] font-normal">
                            <div className="flex items-center justify-between">
                                <span className="text-grey40 dark:text-nightMuted">Subtotal:</span>
                                <span className="font-semibold text-grey10 dark:text-white">{cur}{subtotal.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-grey40 dark:text-nightMuted">Discount:</span>
                                    <span className="text-green-600 dark:text-green-400">-{cur}{discount.toFixed(2)}</span>
                                </div>
                            )}
                            {memberDiscount > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-grey40 dark:text-nightMuted">Member Discount:</span>
                                    <span className="text-green-600 dark:text-green-400">-{cur}{memberDiscount.toFixed(2)}</span>
                                </div>
                            )}
                            {addedFee > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-grey40 dark:text-nightMuted">{resolvedFee?.feeLabel || 'Service Fee'}:</span>
                                    <span className="font-semibold text-grey10 dark:text-white">{cur}{addedFee.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between text-[22px] font-bold tracking-[-0.03em] text-grey10 dark:text-white mt-2">
                                <span>Total:</span>
                                <span>{cur}{total.toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Liability waiver + terms sit at the end of the scrollable content on every size */}
                        <div className="md:mt-auto flex flex-col gap-4">
                            {waiverAndTerms}
                            {/* Desktop: checkout button flows inline after the terms */}
                            <div className="hidden md:block">
                                {checkoutButton}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile: checkout footer pinned below the scrolling body, with the total shown above it */}
                <div className="md:hidden shrink-0 bg-white dark:bg-nightSurface border-t border-black/[0.06] dark:border-white/10 px-5 pt-3 flex flex-col gap-1 shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.15)]" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
                    <div className="font-bold text-[22px] tracking-[-0.03em] text-grey10 dark:text-white text-left mb-1">{cur}{total}</div>
                    {checkoutButton}
                </div>
            </div>

            {showWaiverModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white dark:bg-nightSurface rounded-[28px] p-6 max-w-md w-full mx-4 shadow-2xl ring-1 ring-black/[0.06] dark:ring-white/10">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Liability Waiver</h2>
                            <button onClick={() => setShowWaiverModal(false)} className={closeBtn}>
                                <Close width={20} height={20} />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto text-sm leading-relaxed text-grey30 dark:text-nightTextSecondary whitespace-pre-wrap">
                            {event?.liabilityWaiverText ? String(event.liabilityWaiverText).trim() : 'No liability waiver text has been provided for this event.'}
                        </div>
                    </div>
                </div>
            )}

            {showPaymentModal && pendingOrderRes?.data?.stripe?.clientSecret && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white dark:bg-nightSurface rounded-[28px] p-6 max-w-md w-full mx-4 shadow-2xl ring-1 ring-black/[0.06] dark:ring-white/10 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Payment</h2>
                            <button
                                onClick={() => setShowPaymentModal(false)}
                                className={closeBtn}
                            >
                                <Close width={20} height={20} />
                            </button>
                        </div>

                        <div className="mb-5 rounded-2xl bg-grey99 px-4 py-3 text-sm text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">
                            Total: <span className="font-bold text-grey10 dark:text-white">{cur}{total.toFixed(2)}</span>
                        </div>

                        <Elements
                            stripe={getStripe(pendingOrderRes.data.stripe.publishableKey)}
                            options={{
                                clientSecret: pendingOrderRes.data.stripe.clientSecret,
                                appearance: theme === 'dark'
                                    ? {
                                        theme: 'night',
                                        variables: {
                                            colorPrimary: '#22c9cd',
                                            colorBackground: '#181818',
                                            colorText: '#ffffff',
                                            colorTextSecondary: '#ffffff',
                                            colorTextPlaceholder: '#A8A8A8',
                                            colorIcon: '#ffffff',
                                            colorDanger: '#F87171',
                                            fontFamily: 'Inter, system-ui, sans-serif',
                                            spacingUnit: '4px',
                                            borderRadius: '4px',
                                        },
                                        rules: {
                                            '.Label': { color: '#ffffff' },
                                            '.Input': { border: '1px solid #333333' },
                                            '.Tab': { border: '1px solid #333333', color: '#ffffff' },
                                        },
                                    }
                                    : {
                                        theme: 'stripe',
                                        variables: {
                                            colorPrimary: '#008C95',
                                            colorBackground: '#ffffff',
                                            colorText: '#1a1a1a',
                                            colorDanger: '#CB0000',
                                            fontFamily: 'Inter, system-ui, sans-serif',
                                            spacingUnit: '4px',
                                            borderRadius: '4px',
                                        },
                                    },
                            }}
                        >
                            <CheckoutPaymentForm
                                amount={total}
                                currency={cur}
                                orderId={pendingOrderRes.data.order._id}
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                            />
                        </Elements>
                    </div>
                </div>
            )}
        </div>
    );
}
